import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Table, Select, Modal, message, Popconfirm, Input } from "antd";
import { format, fromUnixTime } from "date-fns";
import formatMoney from "../../../utils/formatMoney";
import batchPaymentApi from "../../../api/batchPaymentApi";
import downloadBlob from "../../../utils/download-blob";

const BATCH_STATUS_MAP = {
    processing:  { label: "Đang xử lý",   style: "bg-blue-100 text-blue-700" },
    exported:    { label: "Đã xuất file",  style: "bg-orange-100 text-orange-700" },
    reconciling: { label: "Đang tra soát", style: "bg-purple-100 text-purple-700" },
    completed:   { label: "Hoàn tất",      style: "bg-green-100 text-green-700" },
    cancelled:   { label: "Đã huỷ",        style: "bg-surface-muted text-content-3" },
};

const ITEM_STATUS_MAP = {
    valid:     { label: "Hợp lệ",       style: "bg-blue-50 text-blue-600" },
    invalid:   { label: "Không hợp lệ", style: "bg-red-50 text-red-600" },
    ok:        { label: "OK",            style: "bg-green-100 text-green-700" },
    hold:      { label: "HOLD",          style: "bg-yellow-100 text-yellow-700" },
    error:     { label: "ERROR",         style: "bg-red-100 text-red-700" },
    cancelled: { label: "Đã huỷ",        style: "bg-surface-muted text-content-3" },
};

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function filenameSlugFromBatchName(name, maxLen = 48) {
    if (!name || typeof name !== "string") return "";
    return name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "")
        .slice(0, maxLen);
}

/** Fallback when Content-Disposition is missing (must match backend buildBatchExportFilename). */
function buildBatchExportFilename(type, batch, routeId) {
    const bid = batch?.batchId ?? routeId ?? "";
    const slug = filenameSlugFromBatchName(batch?.batchName || "");
    const suffix = slug ? `_${slug}` : "";
    if (type === "reconcile") return `reconcile_check_lo${bid}${suffix}.xlsx`;
    return `Payment_Mixed_lo${bid}${suffix}.xlsx`;
}

function parseFilenameFromContentDisposition(header) {
    if (!header || typeof header !== "string") return null;
    const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1].trim());
        } catch {
            return null;
        }
    }
    const quoted = /filename="((?:[^"\\]|\\.)*)"/i.exec(header);
    if (quoted) return quoted[1].replace(/\\"/g, '"');
    const plain = /filename=([^;\n]+)/i.exec(header);
    if (plain) return plain[1].trim().replace(/^["']|["']$/g, "");
    return null;
}

function BatchDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [batch, setBatch] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // Reconcile draft: { [paymentId]: { batchStatus, note } }
    const [reconcileDraft, setReconcileDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
    // Huỷ lô bắt buộc kèm lý do — để sau còn truy được là file bị ngân hàng từ chối hay gom nhầm
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelling, setCancelling] = useState(false);

    const fetchDetail = async () => {
        try {
            const { data } = await batchPaymentApi.getById(Number(id));
            setBatch(data.data.batch);
            const batchItems = data.data.batch.items || [];
            setItems(batchItems);
            // Pre-fill reconcile draft with existing batchStatus
            const draft = {};
            batchItems.forEach((p) => {
                if (["ok", "hold", "error"].includes(p.batchStatus)) {
                    draft[p.paymentId] = { batchStatus: p.batchStatus, note: p.note || "" };
                }
            });
            setReconcileDraft(draft);
        } catch {
            message.error("Không thể tải chi tiết lô");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDetail(); }, [id]);

    const handleExport = async (type) => {
        try {
            const response = await batchPaymentApi.exportXlsx(Number(id), type);
            const cd = response.headers?.["content-disposition"]
                || response.headers?.["Content-Disposition"];
            const filename = parseFilenameFromContentDisposition(cd)
                || buildBatchExportFilename(type, batch, id);
            downloadBlob(response.data, filename, XLSX_MIME);
            // Refresh to pick up status change
            fetchDetail();
        } catch {
            message.error("Xuất file thất bại");
        }
    };

    const handleSaveReconcile = async () => {
        const reconcileItems = Object.entries(reconcileDraft).map(([paymentId, val]) => ({
            paymentId: Number(paymentId),
            batchStatus: val.batchStatus,
            note: val.note,
        }));
        if (reconcileItems.length === 0) return message.warning("Chưa đánh dấu trạng thái cho lệnh nào");
        setSaving(true);
        try {
            await batchPaymentApi.reconcile(Number(id), reconcileItems);
            message.success("Lưu tra soát thành công");
            fetchDetail();
        } catch {
            message.error("Lưu tra soát thất bại");
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = async () => {
        setCompleting(true);
        try {
            const { data } = await batchPaymentApi.complete(Number(id));
            const { paidCount, returnedCount } = data.data;
            message.success(`Hoàn tất: ${paidCount} lệnh đã PAID, ${returnedCount} lệnh trả về hàng chờ`);
            setShowCompleteConfirm(false);
            fetchDetail();
        } catch {
            message.error("Hoàn tất lô thất bại");
        } finally {
            setCompleting(false);
        }
    };

    const handleCancel = async () => {
        const reason = cancelReason.trim();
        if (!reason) return message.warning("Nhập lý do huỷ lô");
        setCancelling(true);
        try {
            await batchPaymentApi.cancel(Number(id), reason);
            message.success("Đã huỷ lô");
            setShowCancelConfirm(false);
            setCancelReason("");
            fetchDetail();
        } catch (err) {
            message.error(err?.response?.data?.message || "Huỷ lô thất bại");
        } finally {
            setCancelling(false);
        }
    };

    // Gỡ 1 lệnh khỏi lô → về hàng chờ, huỷ riêng ở trang Yêu cầu rút tiền.
    // Chỉ dùng cho dòng bank đã từ chối (HOLD/ERROR); backend chặn các trạng thái còn lại.
    const handleDetachItem = async (paymentId) => {
        try {
            await batchPaymentApi.detachItem(Number(id), paymentId);
            message.success(`Đã gỡ lệnh #${paymentId} khỏi lô — xử lý tiếp ở trang Yêu cầu rút tiền`);
            fetchDetail();
        } catch (err) {
            message.error(err?.response?.data?.message || "Gỡ lệnh khỏi lô thất bại");
        }
    };

    const handleDelete = async () => {
        try {
            await batchPaymentApi.purge(Number(id));
            message.success("Đã xoá lô");
            navigate("/payment/batch");
        } catch {
            message.error("Xoá lô thất bại");
        }
    };

    // Count summary for reconciling status
    const okCount = items.filter((p) => p.batchStatus === "ok").length;
    const holdCount = items.filter((p) => p.batchStatus === "hold").length;
    const errorCount = items.filter((p) => p.batchStatus === "error").length;

    // Dòng còn "Hợp lệ" = chưa ai tra soát. Chốt lô lúc này thì nó không được trả mà cũng
    // không về hàng chờ → kẹt lơ lửng (đã xảy ra ở lô 33, 12/06/2026). Backend cũng chặn.
    const unreconciledCount = items.filter((p) => p.batchStatus === "valid").length;

    const canReconcile = batch && ["exported", "reconciling"].includes(batch.status);
    const canComplete = batch && batch.status === "reconciling" && okCount > 0 && unreconciledCount === 0;
    const canExport = batch && ["processing", "exported", "reconciling"].includes(batch.status);
    const canCancel = batch && !["completed", "cancelled"].includes(batch.status);
    const isReadOnly = batch && ["completed", "cancelled"].includes(batch.status);

    const columns = [
        {
            title: "ID",
            dataIndex: "paymentId",
            render: (v) => `#${v}`,
            width: 70,
        },
        {
            title: "User",
            dataIndex: "user",
            render: (u) => (u ? u.name || `#${u.userId}` : "-"),
        },
        {
            title: "Chủ TK",
            dataIndex: "bankAccountName",
        },
        {
            title: "STK",
            dataIndex: "bankAccountNumber",
        },
        {
            title: "Ngân hàng",
            render: (_, r) => r.Bank?.bankName || "-",
        },
        {
            title: "Số tiền",
            dataIndex: "amount",
            render: (v) => formatMoney.format(v),
        },
        {
            title: "Trạng thái",
            dataIndex: "batchStatus",
            render: (status, row) => {
                if (!canReconcile) {
                    const s = ITEM_STATUS_MAP[status] || { label: status || "-", style: "" };
                    return <span className={`px-2 py-0.5 rounded-md text-sm ${s.style}`}>{s.label}</span>;
                }
                // Editable dropdown when reconciling
                const draft = reconcileDraft[row.paymentId];
                return (
                    <Select
                        size="small"
                        value={draft?.batchStatus || status}
                        style={{ width: 100 }}
                        onChange={(val) =>
                            setReconcileDraft((prev) => ({
                                ...prev,
                                [row.paymentId]: { ...prev[row.paymentId], batchStatus: val },
                            }))
                        }
                    >
                        <Select.Option value="ok">OK</Select.Option>
                        <Select.Option value="hold">HOLD</Select.Option>
                        <Select.Option value="error">ERROR</Select.Option>
                    </Select>
                );
            },
        },
        {
            title: "Ghi chú",
            dataIndex: "note",
            render: (note, row) => {
                if (!canReconcile) return note || "-";
                const draft = reconcileDraft[row.paymentId];
                return (
                    <input
                        className="border rounded px-1 py-0.5 text-sm w-36"
                        value={draft?.note || ""}
                        placeholder="Ghi chú"
                        onChange={(e) =>
                            setReconcileDraft((prev) => ({
                                ...prev,
                                [row.paymentId]: { ...prev[row.paymentId], note: e.target.value },
                            }))
                        }
                    />
                );
            },
        },
        {
            title: "Thao tác",
            key: "actions",
            width: 110,
            render: (_, row) => {
                // Chỉ hiện với dòng ngân hàng đã từ chối dứt điểm — dòng "Hợp lệ"/"OK" mà gỡ ra
                // rồi huỷ thì hoàn ví trong khi tiền có thể đã chuyển → chi đôi.
                if (!canReconcile || !["hold", "error"].includes(row.batchStatus)) return null;
                return (
                    <Popconfirm
                        title={`Gỡ lệnh #${row.paymentId} khỏi lô?`}
                        description="Lệnh trở về hàng chờ, sau đó huỷ ở trang Yêu cầu rút tiền. Các lệnh khác trong lô giữ nguyên."
                        onConfirm={() => handleDetachItem(row.paymentId)}
                        okText="Gỡ"
                        cancelText="Không"
                    >
                        <button className="px-2 py-0.5 text-sm border border-line-strong rounded-md hover:bg-surface-muted">
                            Gỡ khỏi lô
                        </button>
                    </Popconfirm>
                );
            },
        },
    ];

    if (loading) return <div className="p-4">Đang tải...</div>;
    if (!batch) return <div className="p-4 text-red-600">Không tìm thấy lô</div>;

    const batchStatusInfo = BATCH_STATUS_MAP[batch.status] || { label: batch.status, style: "" };

    return (
        <div className="p-4 w-full flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/payment/batch" className="text-blue-600 underline text-sm">
                    ← Danh sách lô
                </Link>
                <h2 className="text-lg font-semibold">
                    Lô #{batch.batchId} — {batch.batchName}
                </h2>
                <span className={`px-2 py-0.5 rounded-md text-sm ${batchStatusInfo.style}`}>
                    {batchStatusInfo.label}
                </span>
            </div>

            {/* Summary */}
            <div className="flex gap-6 text-sm text-content-2 bg-surface-sunken p-3 rounded-md">
                <span><b>{batch.totalItems}</b> lệnh</span>
                <span><b>{formatMoney.format(batch.totalAmount)}</b></span>
                <span>Tạo bởi: <b>{batch.creator?.name || `#${batch.createdBy}`}</b></span>
                <span>Ngày tạo: <b>{batch.createdAt ? format(fromUnixTime(batch.createdAt), "dd/MM/yyyy HH:mm") : "-"}</b></span>
                {batch.completedAt && (
                    <span>Hoàn tất: <b>{format(fromUnixTime(batch.completedAt), "dd/MM/yyyy HH:mm")}</b></span>
                )}
                <button
                    onClick={fetchDetail}
                    className="px-2 py-1 text-xs bg-surface border border-line-strong rounded-md hover:bg-surface-muted"
                >
                    ↻ Tải lại
                </button>
            </div>

            {batch.status === "cancelled" && batch.cancelReason && (
                <div className="text-sm bg-surface-sunken p-3 rounded-md">
                    Lý do huỷ: <b>{batch.cancelReason}</b>
                </div>
            )}

            {/* Reconcile summary (when reconciling) */}
            {batch.status === "reconciling" && (
                <div className="flex gap-4 text-sm flex-wrap items-center">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-md">OK: {okCount}</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-md">HOLD: {holdCount}</span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-md">ERROR: {errorCount}</span>
                    <span className="text-content-3">
                        Đã soát {okCount + holdCount + errorCount}/{batch.totalItems}
                    </span>
                </div>
            )}

            {/* Sync có thể sót dòng nếu ops chưa lật hết trang trên portal ngân hàng — không
                phát hiện thì lệnh kẹt lơ lửng, user không nhận được tiền. */}
            {canReconcile && unreconciledCount > 0 && (
                <div className="text-sm bg-yellow-50 border border-yellow-300 text-yellow-800 p-3 rounded-md">
                    <b>Còn {unreconciledCount} lệnh chưa tra soát</b> (đang ở trạng thái “Hợp lệ”).
                    Chưa hoàn tất lô được — hãy đánh dấu OK/HOLD/ERROR cho tất cả.
                    <br />
                    Nếu trạng thái không tự cập nhật: mở lại màn chi tiết lô trên portal ngân hàng và
                    <b> lật hết các trang</b> để userscript đồng bộ đủ, rồi bấm <b>Tải lại</b>.
                </div>
            )}

            {/* Action buttons */}
            {!isReadOnly && (
                <div className="flex gap-2 flex-wrap">
                    {canExport && (
                        <>
                            <button
                                onClick={() => handleExport("mixed")}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm"
                            >
                                Xuất Payment_Mixed.xlsx
                            </button>
                            <button
                                onClick={() => handleExport("reconcile")}
                                className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm"
                            >
                                Xuất file tra soát
                            </button>
                        </>
                    )}
                    {canReconcile && (
                        <button
                            onClick={handleSaveReconcile}
                            disabled={saving}
                            className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-sm disabled:opacity-60"
                        >
                            {saving ? "Đang lưu..." : "Lưu tra soát"}
                        </button>
                    )}
                    {canComplete && (
                        <button
                            onClick={() => setShowCompleteConfirm(true)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm"
                        >
                            Xác nhận hoàn tất lô
                        </button>
                    )}
                    {canCancel && (
                        <button
                            onClick={() => setShowCancelConfirm(true)}
                            className="px-3 py-1.5 bg-gray-700 text-white rounded-md text-sm"
                        >
                            Huỷ lô
                        </button>
                    )}
                </div>
            )}

            {/* Delete button — only for cancelled batches */}
            {batch.status === "cancelled" && (
                <div className="flex gap-2">
                    <Popconfirm
                        title="Xoá lô này?"
                        description="Lô sẽ bị xoá vĩnh viễn, không thể khôi phục."
                        onConfirm={handleDelete}
                        okText="Xoá"
                        okButtonProps={{ danger: true }}
                        cancelText="Không"
                    >
                        <button className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm">
                            Xoá lô
                        </button>
                    </Popconfirm>
                </div>
            )}

            {/* Items table */}
            <div className="w-full overflow-auto">
                <Table dataSource={items} columns={columns} rowKey="paymentId" pagination={{ pageSize: 50 }} size="small" scroll={{ x: "max-content" }} />
            </div>

            {/* Cancel confirmation modal — lý do là bắt buộc */}
            <Modal
                open={showCancelConfirm}
                title="Huỷ lô thanh toán"
                onCancel={() => setShowCancelConfirm(false)}
                onOk={handleCancel}
                okText="Huỷ lô"
                cancelText="Không"
                confirmLoading={cancelling}
                okButtonProps={{ danger: true, disabled: !cancelReason.trim() }}
            >
                <div className="flex flex-col gap-2 text-sm">
                    <p>Tất cả lệnh rút trong lô sẽ được trả về hàng chờ.</p>
                    <p className="m-0">Lý do huỷ <span className="text-red-600">*</span></p>
                    <Input.TextArea
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="VD: ngân hàng từ chối cả file do sai định dạng / gom nhầm lệnh / tạo lại lô khác"
                        maxLength={500}
                        showCount
                    />
                </div>
            </Modal>

            {/* Complete confirmation modal */}
            <Modal
                open={showCompleteConfirm}
                title="Xác nhận hoàn tất lô thanh toán"
                onCancel={() => setShowCompleteConfirm(false)}
                onOk={handleComplete}
                okText="Xác nhận"
                cancelText="Huỷ"
                confirmLoading={completing}
                okButtonProps={{ danger: false }}
            >
                <div className="flex flex-col gap-2 text-sm">
                    <p className="font-semibold">Hành động này không thể hoàn tác:</p>
                    <p>
                        <span className="text-green-700 font-semibold">OK ({okCount} lệnh)</span>
                        {" "}→ sẽ được đánh dấu{" "}
                        <span className="font-semibold">ĐÃ THANH TOÁN</span>
                    </p>
                    <p>
                        <span className="text-yellow-700 font-semibold">HOLD/ERROR ({holdCount + errorCount} lệnh)</span>
                        {" "}→ sẽ trở về hàng chờ để xử lý sau
                    </p>
                </div>
            </Modal>
        </div>
    );
}

export default BatchDetail;
